import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MapPin, Menu, X, Clock, Sun, Moon } from 'lucide-react';
import { businessInfo } from '../data/menuData';
import { useTheme } from '../context/ThemeContext';

// MKD Bakery Logo Component
const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/uuvmafm4_image.png"
    alt="MKD Bakery Logo"
    className="h-12 w-12 rounded-full object-cover"
    style={{ imageRendering: 'crisp-edges' }}
    loading="eager"
  />
);

// Animation variants defined outside component to prevent re-creation
const mobileMenuVariants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 }
};

// Navigation links data
const navLinks = [
  { name: 'Home', href: '#hero' },
  { name: 'About', href: '#about' },
  { name: 'Menu', href: '#menu' },
  { name: 'Specials', href: '#specials' },
  { name: 'Gallery', href: '#gallery' },
  { name: 'Contact', href: '#contact' },
];

// Theme Toggle Button Component
const ThemeToggleButton = ({ isDarkMode, onClick, size = 20, className = "" }) => (
  <button
    onClick={onClick}
    className={className}
    aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
  >
    {isDarkMode ? (
      <Sun size={size} className="text-yellow-400" />
    ) : (
      <Moon size={size} className="text-bakery-text" />
    )}
  </button>
);

// Top Info Bar Component
const TopInfoBar = ({ isDarkMode, toggleTheme }) => (
  <div className="bg-bakery-text dark:bg-gray-900 text-bakery-bg py-2 px-4 text-sm hidden md:block">
    <div className="max-w-7xl mx-auto flex justify-between items-center">
      <div className="flex items-center gap-6">
        <span className="flex items-center gap-2">
          <MapPin size={14} />
          {businessInfo.address}
        </span>
        <span className="flex items-center gap-2">
          <Clock size={14} />
          {businessInfo.hours}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <ThemeToggleButton 
          isDarkMode={isDarkMode} 
          onClick={toggleTheme}
          size={18}
          className="p-2 rounded-full hover:bg-bakery-bg/10 transition-colors duration-300"
        />
        <a 
          href={`tel:${businessInfo.phone}`} 
          className="flex items-center gap-2 hover:text-bakery-primary transition-colors duration-300"
          data-testid="top-phone-link"
        >
          <Phone size={14} />
          {businessInfo.phone}
        </a>
      </div>
    </div>
  </div>
);

// Desktop Navigation Component
const DesktopNav = ({ scrollToSection, isDarkMode, toggleTheme }) => (
  <>
    <div className="hidden lg:flex items-center gap-8">
      {navLinks.map((link) => (
        <a
          key={link.name}
          href={link.href}
          onClick={(e) => scrollToSection(e, link.href)}
          className="text-bakery-text dark:text-bakery-bg hover:text-bakery-primary transition-colors duration-300 font-medium"
          data-testid={`nav-link-${link.name.toLowerCase()}`}
        >
          {link.name}
        </a>
      ))}
    </div>
    <div className="hidden lg:flex items-center gap-4">
      <ThemeToggleButton 
        isDarkMode={isDarkMode} 
        onClick={toggleTheme}
        size={20}
        className="p-3 rounded-full bg-bakery-muted dark:bg-gray-700 hover:bg-bakery-border dark:hover:bg-gray-600 transition-colors duration-300"
      />
      <a
        href={`tel:${businessInfo.phone}`}
        className="flex items-center gap-2 bg-bakery-text dark:bg-bakery-primary text-bakery-bg px-6 py-3 rounded-full font-medium btn-lift"
        data-testid="navbar-call-btn"
      >
        <Phone size={18} />
        Call to Order
      </a>
    </div>
  </>
);

// Mobile Menu Component
const MobileMenu = ({ isOpen, scrollToSection }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        variants={mobileMenuVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="lg:hidden bg-bakery-bg dark:bg-gray-800 border-t border-bakery-border dark:border-gray-700"
        data-testid="mobile-menu"
      >
        <div className="px-6 py-6 space-y-4">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="block text-bakery-text dark:text-bakery-bg hover:text-bakery-primary transition-colors duration-300 font-medium py-2"
            >
              {link.name}
            </a>
          ))}
          <a
            href={`tel:${businessInfo.phone}`}
            className="flex items-center justify-center gap-2 bg-bakery-text dark:bg-bakery-primary text-bakery-bg px-6 py-4 rounded-full font-medium mt-4"
            data-testid="mobile-call-btn"
          >
            <Phone size={18} />
            Call to Order
          </a>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = useCallback((e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  }, []);

  return (
    <>
      <TopInfoBar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'navbar-glass shadow-lg dark:bg-gray-800/90' 
            : 'bg-bakery-bg dark:bg-gray-800'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <a 
              href="#hero" 
              onClick={(e) => scrollToSection(e, '#hero')}
              className="flex items-center gap-3"
              data-testid="navbar-logo"
            >
              <Logo />
            </a>

            <DesktopNav 
              scrollToSection={scrollToSection} 
              isDarkMode={isDarkMode} 
              toggleTheme={toggleTheme} 
            />

            <div className="lg:hidden flex items-center gap-2">
              <ThemeToggleButton 
                isDarkMode={isDarkMode} 
                onClick={toggleTheme}
                size={20}
                className="p-2 rounded-full bg-bakery-muted dark:bg-gray-700"
              />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-bakery-text dark:text-bakery-bg"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        <MobileMenu isOpen={isMobileMenuOpen} scrollToSection={scrollToSection} />
      </nav>
    </>
  );
};

export default Navbar;
